import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { v4 as uuidv4 } from 'uuid';

import { TITLE_NODE_NAME } from '../nodes/title/constants';
import { markSystemTr } from './shared';

/**
 * A document that can have a title always starts with one.
 *
 * The schema used to say so - `title (rootOnly | block)*` - and ProseMirror kept an empty title node
 * through a select-all-delete. It cannot say so any more, because the editor may hold a part of the
 * document that does not contain the title, and a mandatory title there makes ProseMirror synthesize
 * one that a commit would splice into the middle of the document.
 *
 * So the schema says `title?` and this restores the behaviour the mandatory form gave: without it,
 * select-all-delete removes the title element outright and there is nothing left to type a title
 * into - typing produces a paragraph.
 */
export const getRequiredTitlePlugin = (holdsDocumentStart: () => boolean) =>
  new Plugin({
    key: new PluginKey('requiredTitle'),
    appendTransaction: (_transactions, _oldState, newState) => {
      if (newState.doc.firstChild?.type.name === TITLE_NODE_NAME || !holdsDocumentStart()) return null;

      // The id is stamped here: the id pass skips a read-only editor, and this also runs while a draft loads before
      // edit mode is on, so a title inserted then was saved without one - and AI Edit could not read the document.
      const tr = newState.tr.insert(0, newState.schema.nodes[TITLE_NODE_NAME].create({ id: uuidv4() }));
      // Inserting at 0 pushes a caret that was at the document start past the new title, so typing
      // after a select-all-delete would go into the body. The mandatory title put it in the title.
      if (newState.selection.empty && newState.selection.from <= 1) {
        tr.setSelection(TextSelection.create(tr.doc, 1));
      }

      return markSystemTr(tr, { keepInHistory: true });
    },
  });
